import React, { HTMLProps, useContext, useEffect, useState } from "react";

export interface RouteProps extends HTMLProps<HTMLDivElement> {
    path?: string;
}

export interface RouterContextProps {
    currentPath: string;
    navigate: NavigateFunction;
}

export type NavigateFunction = (props: { to: string }) => void;

const RouterContext = React.createContext<RouterContextProps | undefined>(undefined);

const Router: React.FunctionComponent = ({ children }) => {
    const [currentPath, setCurrentPath] = useState(window.location.hash);

    useEffect(() => {
        if (!("onhashchange" in window)) {
            console.error("The browser don't supports the hashchange event!");
        }

        const handleHashChange = () => {
            setCurrentPath(window.location.hash);
        };

        window.addEventListener("hashchange", handleHashChange);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    const navigate: NavigateFunction = ({ to }) => {
        const newHash = `#${to}`;

        setCurrentPath(newHash);
        window.location.hash = newHash;
    };

    return <RouterContext.Provider value={{ currentPath, navigate }}>{children}</RouterContext.Provider>;
};

const Route: React.FunctionComponent<RouteProps> = ({ path, children }) => {
    const { currentPath } = useContext(RouterContext)!;
    const currentPathWithoutParams = currentPath.split("?")[0];

    const shouldRender = path === "" ? currentPathWithoutParams === "" || currentPathWithoutParams === "#" : currentPathWithoutParams === `#${path}`;

    return shouldRender ? <>{children}</> : null;
};

const useNavigate = () => {
    const { navigate } = useContext(RouterContext)!;
    return navigate;
};

const getUrlParams = () => {
    const searchParams = new URLSearchParams(window.location.hash.split("?")[1]);
    return searchParams;
};

const setUrlParams = (params: URLSearchParams | Record<string, string>) => {
    const searchParams = new URLSearchParams(params);
    const currentPath = window.location.hash.split("?")[0];

    const newHash = searchParams.toString() ? `${currentPath}?${searchParams.toString()}` : currentPath;
    window.location.hash = newHash;
};

const clearUrlParams = () => {
    window.location.hash = window.location.hash.split("?")[0];
};

export { Route, Router, getUrlParams, setUrlParams, clearUrlParams, useNavigate };
